import { useEffect, useState } from 'react';

import { usePermissions } from '@refinedev/core';

import {
  ABIcon, AccountCircleIcon, ActivityMonitorIcon, AddQueueIcon, FillOff2Icon,
} from '@rrexpress/core';

export interface IUserRoles {
  permissions: {
    permission_group: string;
    permission_id: string;
    permission_name: string;
    permission_sub_group: string;
  }[];
  role_id: string;
  role_name: string;
}

export type MenuItemType = {
  id?: number;
  title: string;
  path?: string;
  icon?: React.ReactNode;
  badgeIcon?: React.ReactNode;
  submenu?: MenuItemType[];
  permissions: string[];
};

const useSidebarMenu = () => {
  const { data, isLoading } = usePermissions<IUserRoles>();
  const permissions = data?.permissions || [];
  const permissionsIds = new Set(permissions.map((p) => p.permission_id));
  const [validMenu, setValidMenu] = useState<MenuItemType[]>([]);
  const permissionsIdsLength = permissions.length;

  const validatePermissions = (allowedPermissions: string[]) => {
    // Menu with no permission should be allowed by default
    if (allowedPermissions.length === 0) return true;

    return allowedPermissions.some(
      (permission) => permissionsIds.has(permission),
    );
  };

  const sidebarMenu: MenuItemType[] = [
    {
      id: 1,
      title: 'Dashboard',
      path: '/',
      icon: <ActivityMonitorIcon />,
      permissions: [],
    },
    {
      id: 2,
      title: 'Order',
      path: '/orders',
      icon: <AddQueueIcon />,
      permissions: [
        'orders.show',
        'orders.list',
      ],
    },
    {
      id: 3,
      title: 'Cek Ongkir',
      path: '/check-shipping-price',
      icon: <AddQueueIcon />,
      permissions: [],
    },
    {
      id: 4,
      title: 'Pengaturan dan Administrasi',
      icon: <AccountCircleIcon />,
      permissions: [],
      submenu: [
        {
          id: 4.1,
          title: 'Manajemen User',
          path: '/user-management',
          permissions: ['user-management.list'],
        },
        {
          id: 4.2,
          title: 'Profile User',
          path: '/profile',
          permissions: [],
        },
      ],
    },
    {
      id: 5,
      title: 'Master Data',
      icon: <ABIcon />,
      permissions: [
        'branches.list',
        'contacts.list',
        'invoices.list',
      ],
      submenu: [
        {
          id: 5.1,
          title: 'Cabang',
          path: '/branches',
          permissions: ['branches.list'],
        },
        {
          id: 5.2,
          title: 'Kontak',
          path: '/contacts',
          permissions: ['contacts.list'],
        },
        {
          id: 5.3,
          title: 'Import Progress',
          path: '/import-progress',
          permissions: [
            'orders.show',
            'orders.list',
          ],
        },
      ],
    },
    {
      id: 6,
      title: 'Billing',
      icon: <FillOff2Icon />,
      permissions: [],
      submenu: [
        {
          id: 6.1,
          title: 'Invoice',
          path: '/invoices',
          permissions: ['invoices.list'],
        },
      ],
    },
  ];

  useEffect(() => {
    if (!isLoading && permissionsIds) {
      const validatedMenus = sidebarMenu.reduce<MenuItemType[]>((acc, menu) => {
        if (validatePermissions(menu.permissions)) {
          const newValidMenu = { ...menu };
          if (menu.submenu) {
            newValidMenu.submenu = menu.submenu.filter(
              (submenu) => validatePermissions(submenu.permissions),
            );
          }
          acc.push(newValidMenu);
        }
        return acc;
      }, []);

      setValidMenu(validatedMenus);
    }
  }, [isLoading, permissionsIdsLength]);

  return {
    menu: validMenu,
  };
};

export default useSidebarMenu;
